import { useState, FormEvent, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { apiClient, ApiError } from '../api/client';
import AuthLayout from '../components/AuthLayout';

const MAX_ATTEMPTS = 3;
const MFA_CODE_LENGTH = 6;

export default function VerifyMFA() {
  const [code, setCode] = useState(Array.from({ length: MFA_CODE_LENGTH }, () => ''));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);
  const [rememberDevice, setRememberDevice] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const stepUp = searchParams.get('step_up') === '1';
  const stepUpRedirectBack = searchParams.get('redirect_back');
  const email = (location.state as { email?: string })?.email || '';
  const redirectTo = (location.state as { redirectTo?: string })?.redirectTo;

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < MFA_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (index === MFA_CODE_LENGTH - 1 && value && newCode.every((digit) => digit !== '')) {
      handleSubmit(new Event('submit') as unknown as FormEvent, newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);
    
    if (pastedData.length === MFA_CODE_LENGTH) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleSubmit(new Event('submit') as unknown as FormEvent, pastedData);
    }
  };

  const handleSubmit = async (e: FormEvent, codeValue?: string) => {
    e.preventDefault();
    const finalCode = codeValue || code.join('');

    if (finalCode.length !== MFA_CODE_LENGTH) {
      setError(`Please enter the complete ${MFA_CODE_LENGTH}-digit code`);
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient.verifyMFA(finalCode, rememberDevice);

      // MFA verified successfully
      if (stepUp && stepUpRedirectBack) {
        navigate(decodeURIComponent(stepUpRedirectBack));
      } else if (response.must_change_password) {
        navigate('/change-password?reason=temporary');
      } else if (redirectTo) {
        const loginParams = new URLSearchParams(redirectTo);
        const internalRedirect = loginParams.get('redirect_to');
        if (internalRedirect) {
          navigate(decodeURIComponent(internalRedirect), { replace: true });
        } else {
          // OAuth flow — call authorize endpoint with Bearer token
          const clientId = loginParams.get('client_id');
          const redirectUri = loginParams.get('redirect_uri');
          const oauthState = loginParams.get('state');
          const codeChallenge = loginParams.get('code_challenge');

          if (clientId && redirectUri && oauthState && codeChallenge) {
            const authResult = await apiClient.authorize({
              clientId,
              redirectUri,
              state: oauthState,
              codeChallenge,
            });
            window.location.href = authResult.redirect_url;
          }
        }
      } else {
        const defaultRedirect = import.meta.env.VITE_DEFAULT_REDIRECT_URL;
        if (defaultRedirect) {
          window.location.href = defaultRedirect;
        } else {
          navigate('/success');
        }
      }
    } catch (err) {
      if (err instanceof ApiError) {
        const isExpiredOrSession =
          err.message.toLowerCase().includes('expired') ||
          err.message.toLowerCase().includes('session');

        // Non-retriable: code expired, invalid session, or too many attempts
        if (err.code === 'FORBIDDEN' || isExpiredOrSession) {
          setError(err.message + ' Redirecting to login...');
          setTimeout(() => navigate('/login', { replace: true }), 2500);
          return;
        }

        // Wrong code — decrement attempts
        const remaining = attemptsLeft - 1;
        setAttemptsLeft(remaining);

        if (remaining <= 0) {
          setError('Too many incorrect attempts. Redirecting to login...');
          setTimeout(() => navigate('/login', { replace: true }), 2500);
          return;
        }

        setError(
          `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
        );
        setCode(Array.from({ length: MFA_CODE_LENGTH }, () => ''));
        inputRefs.current[0]?.focus();
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* Title */}
        <div>
          <span className="text-base mb-1 block">
            {stepUp
              ? `This action requires a recent MFA check. We've sent a ${MFA_CODE_LENGTH}-digit code to your email.`
              : <>We've sent a {MFA_CODE_LENGTH}-digit code to <span className="font-semibold">{email}</span></>
            }
          </span>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-red-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Code Input — grid fills the full card width, each cell is square */}
        <div>
          <label className="label">Verification code</label>
          <div className="grid grid-cols-6 gap-2 mt-2">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="aspect-square w-full text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none transition-all disabled:opacity-50"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading || attemptsLeft <= 0}
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        {/* Expiry note */}
        <p className="text-sm text-gray-500">
          This code expires 10 minutes after it was sent.
        </p>

        {/* Remember device — not shown for step-up re-verification */}
        {!stepUp && (
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
              disabled={isLoading}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Remember this device for 2 weeks</span>
          </label>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          className="btn-primary"
          disabled={isLoading || attemptsLeft <= 0 || code.some((d) => !d)}
        >
          {isLoading ? (
            <span className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Verifying...
            </span>
          ) : (
            'Verify Code'
          )}
        </button>

        {/* Back to Login */}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="btn-secondary"
          disabled={isLoading}
        >
          Back to Login
        </button>

        {/* Help Text */}
        <div className="pt-2 border-t border-gray-200 text-sm text-gray-500">
          Didn't receive a code? Check your spam folder.
        </div>
      </form>
    </AuthLayout>
  );
}

